/**
 * A PDF card's thumbnail: page 1 drawn with pdf.js, uploaded and stored as
 * the frame's light poster with `{kind: "pdf", pages}`, keyed by the
 * SHA-256 of `content.url` exactly as stored ("" for a file linked where it
 * is). Paper is white, so one poster serves both themes; dark cards dim it.
 * Made in the owner window when a PDF card at a URL has none, one document
 * at a time, each frame and URL once per session. A file linked where it is
 * gets its poster when it is added or located (`capturePdfThumbnail`, read
 * through the grant just given), never while the wall paints: that would
 * read the disk, and on macOS could ask for a folder unasked. Its poster is
 * the only part of it on the server: the card other computers show.
 */
import { useEffect, useMemo } from "react";
import { asString } from "@/components/shapes/shared/plain-record";
import { getWindowRoleSync } from "@/desktop/use-window-role";
import {
  posterFailure,
  storeFramePoster,
  type PosterOutcome,
} from "@/lib/frame-poster-store";
import { pdfSource, pdfSourceKey, type PdfSource } from "@/lib/pdf-document";
import { sha256Hex } from "@/lib/sha256-hex";
import { useProjectStore } from "@/stores/project-store";
import {
  closePdfDocument,
  openPdfDocument,
  releaseCanvas,
  renderPdfPage,
} from "./pdf-pages";

/** The thumbnail's width in image pixels: 2× a wide wall column. */
const THUMBNAIL_WIDTH = 800;
/** A very tall page is drawn narrower, at most this tall (ratio 0.5). */
const THUMBNAIL_MAX_HEIGHT = THUMBNAIL_WIDTH * 2;
const THUMBNAIL_JPEG_QUALITY = 0.85;

/** Draws page 1 of the PDF at `source`; null when it cannot be read. */
async function firstPageImage(
  source: PdfSource,
): Promise<{ file: File; pages: number } | null> {
  const opened = await openPdfDocument(source);
  if ("error" in opened) return null;
  const { pdf } = opened;
  const canvas = document.createElement("canvas");
  try {
    const task = await renderPdfPage(pdf, 1, canvas, THUMBNAIL_WIDTH, {
      maxCssHeight: THUMBNAIL_MAX_HEIGHT,
    });
    await task.promise;
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", THUMBNAIL_JPEG_QUALITY),
    );
    if (!blob) return null;
    return {
      file: new File([blob], "pdf-page-1.jpg", { type: "image/jpeg" }),
      pages: pdf.numPages,
    };
  } finally {
    releaseCanvas(canvas);
    closePdfDocument(pdf);
  }
}

/**
 * Makes, uploads and stores the first-page thumbnail of PDF frame
 * `frameId`, read from `source` and keyed by `sourceUrl` (the stored
 * `content.url`, which `source` may have normalised). Never rejects;
 * resolves how it ended.
 */
export async function capturePdfThumbnail(
  projectId: string,
  frameId: string,
  source: PdfSource,
  sourceUrl: string,
): Promise<PosterOutcome> {
  try {
    const image = await firstPageImage(source);
    if (!image) return "rejected";
    await storeFramePoster(projectId, frameId, image.file, {
      theme: "light",
      contentSha256: await sha256Hex(sourceUrl),
      cover: { kind: "pdf", pages: image.pages },
    });
    return "stored";
  } catch (error) {
    return posterFailure(error);
  }
}

/** Frames and URLs asked for in this session, and the one-at-a-time line. */
const requested = new Set<string>();
let line: Promise<unknown> = Promise.resolve();

/**
 * Queues the thumbnail of the PDF in file frame `frameId`'s `source` (card
 * or content) while `missing`, in the owner window of the open project only.
 */
export function usePdfThumbnail(
  frameId: string,
  source: Record<string, unknown>,
  missing: boolean,
): void {
  const read = useMemo(() => pdfSource(source, null), [source]);
  const sourceUrl = asString(source.url);
  useEffect(() => {
    if (!read || !missing || getWindowRoleSync() !== "owner") return;
    const projectId = useProjectStore.getState().currentProject?.id;
    const key = `${frameId}:${pdfSourceKey(read)}`;
    if (!projectId || requested.has(key)) return;
    requested.add(key);
    line = line.then(() =>
      capturePdfThumbnail(projectId, frameId, read, sourceUrl),
    );
  }, [frameId, missing, read, sourceUrl]);
}
